import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteComponent } from '@app/route/route.component';
import { StateService, ZaverProduct } from '@app/service/state/state.service';
import { HeaderService } from '@app/service/header/header.service';
import {
  AccountInformation,
  UserService,
} from '@app/service/user-service/user.service';
import { SelectorItem } from '@app/component/selector/selector.component';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { SeRoute } from '@assets/val/route-constants';
import { PaymentMethod } from '@app/model/payment-request/payment-request.dto';

@Component({
  selector: 'se-confirm-tokenized-payment',
  templateUrl: './se-confirm-tokenized-payment.component.html',
  styleUrls: [
    '../../page-shared.css',
    './se-confirm-tokenized-payment.component.scss',
  ],
})
export class SeConfirmTokenizedPaymentComponent
  extends RouteComponent
  implements OnInit {
  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private valuePipe: ValuePipe
  ) {
    super(router, route, stateService, headerService);
  }

  accountInformation: AccountInformation = null;
  loading: boolean = true;
  ZAVERPRODUCT = ZaverProduct;
  selectorItems: SelectorItem[] = null;
  modalActive: boolean = false;
  selectedItem: SelectorItem;

  // Sync the state in case of window change since for some banks we can end up with duplicate tabs.
  @HostListener('document:visibilitychange', ['$event'])
  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.stateService.syncRouteWithState();
    }
  }

  async ngOnInit(): Promise<void> {
    this.accountInformation = await this.userService.getAccount().toPromise();
    this.loading = false;

    this.selectorItems = [
      this.state.zaverProduct ===
      ZaverProduct.TOKENIZED_ONBOARDING_WITH_PAYMENT && {
        tab: 'Ditt köp',
        title: 'Sammanfattning',
        descriptions: [{ text: this.state.paymentInformation.description }],
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `Totalbelopp inkl. moms: ${this.valuePipe.transform(
          this.state.totalCheckoutValue
        )}`,
      },
      {
        tab: 'Betalningsvillkor',
        title: 'Medgivande till betalning via Autogiro',
        descriptions: [
          {
            text: 'Undertecknad (”betalaren”), medger att betalning får göras genom uttag från angivet konto eller av betalaren senare angivet konto, på begäran av angiven betalningsmottagare för betalning till denne på viss dag (”förfallodagen”) via Autogiro. Betalaren samtycker till att behandling av personuppgifter som lämnats i detta medgivande behandlas av betalarens betaltjänstleverantör, betalningsmottagaren, betalningsmottagarens betaltjänstleverantör och Bankgirocentralen BGC AB för administration av tjänsten. Personuppgiftsansvariga för denna personuppgiftsbehandling är betalarens betaltjänstleverantör, betalningsmottagaren samt betalningsmottagarens betaltjänstleverantör. Betalaren kan när som helst begära att få tillgång till eller rättelse av personuppgifterna genom att kontakta betalarens betaltjänstleverantör. Ytterligare information om behandling av personuppgifter i samband med betalningar kan finnas i villkoren för kontot och i avtalet med betalningsmottagaren. Betalaren kan när som helst återkalla sitt samtycke, vilket medför att tjänsten i sin helhet avslutas.',
          },
          {
            title: 'Villkor för betalning',
            text: 'Betalning via Autogiro hanteras av Zaver (Frink AB) och/eller vår samarbetspartner Trustly Group AB.',
          },
          {
            title: 'Tidpunkt för autogirodebitering',
            text: 'När du betalar via autogiro kommer pengarna att dras från ditt konto inom 1-2 bankdagar från det att handlaren bekräftat hela eller delar av köpet till oss.',
          },
          {
            title: 'Misslyckad autogirodebitering',
            text: 'Skulle täckning saknas på ditt konto när betalningstransaktionen ska genomföras kommer betalningen hanteras av Zaver och du kommer få en faktura från Zaver (Frink AB). Genom att godkänna betalning genom Autogiro godkänner du således att Zaver, vid misslyckad autogirodebitering, ställer ut en faktura till dig. Zaver ansvarar då för att genomföra relevant betaltjänst så du som konsument uppfyller dina åtaganden mot handlaren. Transaktionen innebär att du utnyttjar ett kreditutrymme.',
          },
          {
            title: 'Avgifter och dröjsmålsränta',
            text: 'Fakturan ska betalas inom den tid som anges på fakturan. Vi förbehåller oss rätten att ta ut en administrationsavgift på upp till 49 kr på fakturan. Vid sen betalning av fakturan har vi rätt att ta ut en påminnelseavgift om 60 kr samt debitera dröjsmålsränta på det förfallna beloppet till dess att full betalning sker. Dröjsmålsräntan utgår med 20 % per år. Sker ingen betalning därefter kan fordran komma att överlämnas för inkassohantering där ytterligare inkassokostnader kan tillkomma.',
          },
          {
            title: 'Kreditprövning',
            text: 'Notera att en kreditprövning görs när du ger ditt medgivande till betalning via Autogiro vilket i vissa fall innebär att en kreditupplysning inhämtas från extern part. Du får då en kopia på kreditupplysningen per fysisk och/eller digital post.',
          },
          {
            title: 'Personuppgifter',
            text: 'Zaver behandlar dina personuppgifter för att uppfylla våra åtaganden enligt detta avtal och för de övriga syften som redovisas i vår Integritetspolicy.',
          },
        ],
        links: [
          {
            text: 'Medgivande BG Autogiro (pdf)',
            onClick: () => {
              window.open(
                'https://cdn.zaver.com/SE/docs/autogiro-terms-standard.pdf',
                '_blank'
              );
            },
          },
        ],
      },
    ];
  }

  async handleCtaClick() {
    try {
      this.loading = true;
      await this.userService.confirmFlow();
      await this.navigate();
      this.loading = false;
    } catch (error) {
      console.log(error);
      this.loading = false;
    }
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  showTerms(): void {
    this.selectedItem = this.selectorItems[1];
    this.toggleModal();
  }

  showPurchaseInformation(): void {
    this.selectedItem = this.selectorItems[0];
    this.toggleModal();
  }

  changeAccount(): void {
    this.router.navigate([SeRoute.VENDOR_FLOW], {
      queryParamsHandling: 'merge',
    });
  }

  getMethodName(): string {
    if (
      this.state.zaverProduct === ZaverProduct.TOKENIZED_ONBOARDING &&
      this.state.paymentInformation.paymentMethod === PaymentMethod.COLLECTIVE_INVOICE
    ) {
      return 'månadsbetalning';
    }

    return 'direktbetalning';
  }
}
